import type { EventPolicy } from './EventPolicyEditor';
import type { ClinicalReferences, PrenatalAlertId } from './clinicalReferences';
import { activeEvents } from './projection';
import type { ClinicalEvent } from './types';
export interface PracticeRule {
  id: string;
  event_type: string;
  path: string;
  operator: 'lt' | 'lte' | 'gt' | 'gte' | 'eq';
  threshold: number;
  severity: 'info' | 'warn' | 'critical';
  message: string;
  citation: string;
  confidence: 'Certain' | 'Likely' | 'Guessing';
  enabled: boolean;
}
export interface ChecklistItem {
  id: string;
  label: string;
  min_ga_days: number;
  max_ga_days: number;
  consent_test?: string | null;
  handout_url?: string | null;
}
export interface PracticeConfiguration {
  note_templates?: import('./NoteTemplateEditor').NoteTemplate[];
  state_worksheets?: import('./StateWorksheetConfigEditor').StateWorksheetMapping[];
  postpartum_course?: import('./PostpartumCourseConfigEditor').PostpartumCourseConfiguration | null;
  newborn_growth?: import('./newbornGrowth').NewbornGrowthConfiguration;
  newborn_screening?: import('./newbornScreening').NewbornScreeningConfiguration;
  birth_identity_policy?: { stillbirth_record_policy: 'FETAL_RECORD' | 'PATIENT_CHART' };
  birth_discharge?: import('./BirthDischargeConfigEditor').BirthDischargeConfiguration;
  birth_rules?: import('./birthRules').BirthRuleConfiguration;
  offline_authorization_hours?: number;
  custom_observations?: import('./customDefinitions').CustomObservationDefinition[];
  column_labels?: Record<string, string>;
  event_policies?: EventPolicy[];
  clinical_references?: ClinicalReferences | null;
  prenatal_alerts?: PrenatalAlertId[];
  version: number;
  late_entry_minutes: number;
  checklist: ChecklistItem[];
  rules: PracticeRule[];
  hidden_columns: string[];
  column_order: string[];
  clinically_reviewed?: boolean;
  review_note?: string;
}
export function evaluateRules(events: ClinicalEvent[], configuration: PracticeConfiguration) {
  if (!configuration.clinically_reviewed) return [];
  const flags: {
    ruleId: string;
    eventId: string;
    severity: PracticeRule['severity'];
    message: string;
    configurationVersion: number;
  }[] = [];
  for (const event of activeEvents(events))
    for (const rule of configuration.rules) {
      if (!rule.enabled || rule.event_type !== event.event_type) continue;
      const value = event.payload[rule.path];
      if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isFinite(rule.threshold)) continue;
      const match =
        rule.operator === 'lt'
          ? value < rule.threshold
          : rule.operator === 'lte'
            ? value <= rule.threshold
            : rule.operator === 'gt'
              ? value > rule.threshold
              : rule.operator === 'gte'
                ? value >= rule.threshold
                : value === rule.threshold;
      if (match)
        flags.push({
          ruleId: rule.id,
          eventId: event.id,
          severity: rule.severity,
          message: rule.message,
          configurationVersion: configuration.version,
        });
    }
  return flags;
}
export function checklistState(
  item: ChecklistItem,
  days: number | null,
  events: ClinicalEvent[],
): 'complete' | 'deferred' | 'not applicable' | 'upcoming' | 'due' | 'overdue' | 'dating unavailable' {
  const records = activeEvents(events)
    .filter((e) => e.event_type === 'CHECKLIST.ITEM' && e.payload.item_id === item.id)
    .sort((a, b) => b.sequence - a.sequence);
  if (records[0]?.payload.status === 'COMPLETE') return 'complete';
  if (records[0]?.payload.status === 'NOT_APPLICABLE') return 'not applicable';
  if (days === null) return 'dating unavailable';
  if (days > item.max_ga_days) return 'overdue';
  if (records[0]?.payload.status === 'DEFERRED') return 'deferred';
  return days < item.min_ga_days ? 'upcoming' : 'due';
}
